<template>
  <div class="cmsWysiwyg">
    <div
      v-if="!isSlot"
      class="cmsWysiwyg__block wysiwyg"
      :class="`${additionalClasses} wysiwyg--${className}`"
      v-html="cleanHtml"
    />
    <div
      v-else
      class="cmsWysiwyg__block wysiwyg"
      :class="`${additionalClasses} wysiwyg--${className}`"
    >
      <slot />
    </div>
  </div>
</template>

<script>
export default {
  name: 'CmsWysiwyg',
  props: {
    isSlot: {
      type: Boolean,
      default: false,
    },
    html: {
      type: String,
      default: '',
    },
    className: {
      type: String,
      default: '',
    },
    additionalClasses: {
      type: String,
      default: '',
    },
  },
  computed: {
    cleanHtml() {
      return this.html;
      // return this.$sanitize(this.html);
    },
  },
};
</script>
<style lang="scss">
.wysiwyg {
  font-family: $fMain;

  h2 {
    font-size: 3.6rem;
    line-height: 4.5rem;
    font-weight: 600;
    color: $cMainDark;
    text-align: left;
    margin-bottom: 3rem;
    margin-top: 5rem;
    position: relative;

    &:first-of-type {
      margin-top: 0;
    }

    @include rwd('tablet') {
      font-size: 2.4rem;
      line-height: 3.4rem;
      margin-bottom: 2rem;
    }
  }

  h3 {
    font-size: 2.2rem;
    line-height: 2.8rem;
    font-weight: 600;
    padding-bottom: 3rem;
  }

  h4,
  h5,
  h6 {
    font-size: 1.6rem;
    line-height: 2rem;
    font-weight: 700;
    color: $cMainDark;
    margin-bottom: 1.5rem;
  }

  ul {
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  p {
    font-size: 1.4rem;
    line-height: 2.2rem;
    color: $cAddDark02;
    margin-bottom: 2rem;

    &:last-child {
      margin-bottom: 0;
    }
  }

  b,
  strong {
    font-size: 1.4rem;
    font-weight: 700;
    line-height: 2.2rem;
    color: $cMainLight;
  }

  ol li,
  ol li p,
  ul li,
  ul li p {
    font-size: 1.4rem;
    line-height: 1.8rem;
    color: $cAddDark02;
  }

  ol li {
    list-style: decimal;
    list-style-position: inside;
    margin-bottom: 0.8rem;
  }

  ul li {
    position: relative;
    padding-left: 2.5rem;
    margin-bottom: 0.8rem;

    &::before {
      content: '\2022';
      color: $cAddDark02;
      display: inline-block;
      font-size: 1.2rem;
      line-height: 2rem;
      position: absolute;
      left: 0;
      top: 0;
    }
  }

  img {
    max-width: 100%;
    height: auto;
    margin: 20px 0;
  }

  a {
    text-decoration: none;
    color: $cSec;
    transition: color $tr;

    &:hover {
      color: $cSecHover;
    }
  }

  table {
    border-top: 1px solid $cAddLight;
    table-layout: fixed;
    width: 100%;

    tr {
      border-bottom: 1px solid $cAddLight;

      &:nth-child(odd) {
        background-color: $cAddSuperLight;
      }
      td {
        font-size: 1.4rem;
        line-height: 2.2rem;
        padding: 1.2rem 0;
        font-weight: 600;
        color: $cAddDark02;

        @include rwd('small-tablet') {
          padding: 1.2rem 2rem 1.2rem 0;
        }

        &:first-child {
          padding-left: 3rem;
          color: $cMainDark;
          font-weight: 400;

          @include rwd('small-tablet') {
            padding-left: 2rem;
          }
        }
      }
    }
  }

  &--addHeadingBorder {
    h2 {
      padding-bottom: 3.5rem;

      @include rwd('tablet') {
        padding-bottom: 2.5rem;
      }

      &::after {
        content: '';
        width: 40px;
        height: 4px;
        background-color: $cSec;
        position: absolute;
        left: 0;
        bottom: 0;
      }
    }
  }

  &--biggerText {
    & p,
    & b,
    & strong {
      font-size: 1.6rem;
      line-height: 2.2rem;
    }
  }

  &--bigText {
    & p,
    & b,
    & ul li,
    & ol li,
    & strong {
      font-size: 1.8rem;
      line-height: 2.6rem;

      @include rwd('tablet') {
        font-size: 1.6rem;
        line-height: 2.4rem;
      }
    }

    & h1 {
      font-size: 3.6rem;
      line-height: 4.5rem;
      margin: 0 auto 3rem;
      padding-bottom: 0;

      @include rwd('tablet') {
        margin: 0 auto 2rem;
      }
    }

    & h2 {
      font-size: 2.8rem;
      line-height: 4rem;
      margin: 0 auto 3rem;
      padding-bottom: 0;

      @include rwd('tablet') {
        margin: 0 auto 2rem;
      }
    }

    & h3 {
      font-size: 2.2rem;
      line-height: 2.8rem;
      margin: 0 auto 3rem;
      padding-bottom: 0;

      @include rwd('tablet') {
        margin: 0 auto 2rem;
      }
    }

    & h4 {
      font-size: 2rem;
      line-height: 2.6rem;
      margin: 0 auto 3rem;
      padding-bottom: 0;

      @include rwd('tablet') {
        margin: 0 auto 2rem;
      }
    }

    & h5 {
      font-size: 1.8rem;
      line-height: 2.6rem;
      margin: 0 auto 3rem;
      padding-bottom: 0;

      @include rwd('tablet') {
        margin: 0 auto 2rem;
      }
    }

    & h6 {
      font-size: 1.6rem;
      line-height: 2.4rem;
    }
  }

  &--whiteText {
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    b,
    strong,
    a {
      color: $cWhite;
    }
  }

  &--orangeBullets {
    ul li,
    ol li {
      &:before {
        color: $cSec;
      }
    }
  }

  &--darkText {
    h2,
    h3,
    h4,
    h5,
    h6,
    p,
    b,
    strong,
    a,
    ul li,
    ol li {
      color: $cMainDark;
    }
  }
}
</style>
